import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
  MeetingHeaderFragmentFragment,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  UserStatus,
} from "types/graphql-schema";

import cache, { currentOrganizationVar, currentUserVar } from "@cache/cache";
import AppError from "@components/error/error";
import GraphqlError from "@components/error/graphql-error";
import Layout from "@components/layout/layout";
import Loading from "@components/loading/loading";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import useDocumentTitle from "@components/use-document-title/use-document-title";

import getMeetingQuery from "../graphql/get-meeting-query";
import { MeetingHeaderFragment } from "../graphql/get-previous-meetings-query";
import MeetingDetails from "./details";
import MeetingPageHeader from "./header";
import { MeetingViewEnum } from "./header-constants";
import MeetingBreadcrumb from "./meeting-breadcrumb";
import { MeetingPageContext } from "./meeting-page-context";
import {
  getMeetingVideoTimeFromSearch,
  getMeetingViewFromSearch,
  updateMeetingSearch,
} from "./meeting-page-url-state";
import MeetingViewSidebar from "./meeting-sidebar/meeting-view-sidebar";
import {
  MeetingSidebarTabEnum,
  getDefaultMeetingSidebarTab,
} from "./meeting-sidebar/meeting-view-sidebar-constants";
import MeetingTopics from "./meeting-topics";
import MeetingPageVisibilityRefreshDetection from "./meeting-visibility-refresh-detection";
import PreviousMeetingSummary from "./prep/previous-meeting-summary";
import MeetingSummary from "./prep/summary";

export const MeetingPageContent = ({
  meeting,
  meetingGroupId,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  meetingGroupId: number;
}) => {
  const history = useHistory();
  const location = useLocation();
  const [meetingView, setMeetingView] = useState(() => {
    const meetingViewFromSearch = getMeetingViewFromSearch(location.search);
    if (meetingViewFromSearch) return meetingViewFromSearch;

    return getMeetingVideoTimeFromSearch(location.search) !== null
      ? MeetingViewEnum.summary
      : MeetingViewEnum.notes;
  });
  // Incremented to force MeetingTopics to remount (destroying Yjs providers)
  // so editors pick up notes written to the DB by the add-previous-topics mutation.
  const [topicsVersion, setTopicsVersion] = useState(0);

  const handleChangeMeetingView = (
    option: ToggleButtonGroupType<MeetingViewEnum>,
  ) => {
    if (!option?.value) return;

    setMeetingView(option.value);
    history.replace({
      pathname: location.pathname,
      search: updateMeetingSearch({
        search: location.search,
        meetingView: option.value,
        videoTimeInSeconds: null,
      }),
      hash: location.hash,
    });
  };

  useEffect(() => {
    const meetingViewFromSearch = getMeetingViewFromSearch(location.search);
    const nextMeetingView =
      meetingViewFromSearch ||
      (getMeetingVideoTimeFromSearch(location.search) !== null
        ? MeetingViewEnum.summary
        : MeetingViewEnum.notes);

    setMeetingView(nextMeetingView);
  }, [location.search, meeting.id]);

  return (
    <>
      <MeetingDetails
        meeting={meeting}
        meetingView={meetingView}
        onChangeMeetingView={handleChangeMeetingView}
        onTopicsChanged={() => setTopicsVersion((v) => v + 1)}
      />
      {meetingView === MeetingViewEnum.notes ? (
        <MeetingTopics
          key={topicsVersion}
          meeting={meeting}
          opensTopicsInSidebar={false}
          currentPageMeeting={meeting}
        />
      ) : meetingView === MeetingViewEnum.summary ? (
        <MeetingSummary
          meeting={meeting}
          meetingGroupId={meetingGroupId}
          isCurrentMeeting
        />
      ) : meetingView === MeetingViewEnum.previousMeetingSummary ? (
        <PreviousMeetingSummary
          meeting={meeting}
          meetingGroupId={meetingGroupId}
        />
      ) : null}
    </>
  );
};

export const meetingPageRefreshInterval = 5 * 60 * 1000; // 5 minutes

const MeetingPage = ({
  meetingId,
  hideSidebar = false,
}: {
  meetingId: number;
  hideSidebar?: boolean;
}) => {
  const [activeSidebarTab, setActiveSidebarTab] =
    useState<MeetingSidebarTabEnum>(() =>
      getDefaultMeetingSidebarTab({
        isDemoUser: currentUserVar().status === UserStatus.Demo,
        workHistoryEnabled:
          currentOrganizationVar().featureFlags.workHistory ?? false,
      }),
    );
  const [sidebarMeetingView, setSidebarMeetingView] = useState<
    "overview" | "load-previous-meeting" | number
  >("overview");

  const { loading, error, data, refetch } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    variables: { meetingId },
  });

  const meeting = data?.meeting;
  const meetingGroup = meeting?.meetingGroup;
  const title = meeting?.title || meetingGroup?.title || "";
  useDocumentTitle(title);

  const hasError = error || !data || !meeting || !meetingGroup;

  // try to get data from cache so we can render meeting header asap
  const meetingCacheId = cache.identify({
    __typename: "MeetingNode",
    id: meetingId,
  });
  const meetingHeaderCache = cache.readFragment<MeetingHeaderFragmentFragment>({
    id: meetingCacheId,
    fragmentName: "MeetingHeaderFragment",
    fragment: MeetingHeaderFragment,
  });
  const meetingHeader = meeting || meetingHeaderCache;

  return (
    <>
      <MeetingPageVisibilityRefreshDetection refetch={refetch} />
      <MeetingPageContext.Provider
        value={{
          activeSidebarTab,
          setActiveSidebarTab,
          sidebarMeetingView,
          setSidebarMeetingView,
          isInSidebar: false,
          isInExtension: false,
        }}
      >
        <Layout.Header
          className="bg-white"
          rightSideClassName="gap-1 @3xl/layout-header:gap-2"
          sidebarExpandedUiPreferenceKey={
            hideSidebar ? undefined : "meetingSidebarExpanded"
          }
          title={<MeetingBreadcrumb meeting={meetingHeader} />}
        >
          {meeting && meetingGroup && (
            <MeetingPageHeader meetingGroup={meetingGroup} meeting={meeting} />
          )}
        </Layout.Header>
        <Layout.Container>
          <Layout.Main
            width="md"
            sidebarExpandedUiPreferenceKey={
              hideSidebar ? undefined : "meetingSidebarExpanded"
            }
          >
            {!data && loading ? (
              <div className="w-full">
                <Loading />
              </div>
            ) : error ? (
              <GraphqlError
                error={error}
                whatDidNotWork="The meeting could not be loaded."
              />
            ) : hasError ? (
              <div>
                <AppError title="The meeting could not be loaded." />
              </div>
            ) : (
              <div>
                <MeetingPageContent
                  meeting={meeting}
                  meetingGroupId={meetingGroup.id}
                />
              </div>
            )}
          </Layout.Main>
          {!hideSidebar && meeting && <MeetingViewSidebar meeting={meeting} />}
        </Layout.Container>
      </MeetingPageContext.Provider>
    </>
  );
};

export default MeetingPage;
